import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link as MuiLink,
  InputAdornment,
  IconButton
} from '@mui/material';
import { CheckCircle, CancelOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import { AuthUtils, RegisterCredentials, AuthResponse } from '../../utils/auth';

interface RegisterFormProps {
  onSuccess?: (user: any) => void;
  onSwitchToLogin?: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess, onSwitchToLogin }) => {
  const [credentials, setCredentials] = useState<RegisterCredentials>({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [touched, setTouched] = useState<{ [K in keyof RegisterCredentials]?: boolean }>({});

  const validateForm = (): boolean => {
    setError('');
    setPasswordErrors([]);

    // Validate email
    if (!AuthUtils.validateEmail(credentials.email)) {
      setError('Neplatný formát emailu');
      return false;
    }

    // Validate password
    const passwordValidation = AuthUtils.validatePassword(credentials.password);
    if (!passwordValidation.valid) {
      setPasswordErrors(passwordValidation.errors);
      return false;
    }

    // Confirm password
    if (credentials.password !== credentials.confirmPassword) {
      setError('Hesla se neshodují');
      return false;
    }

    // Validate username
    const sanitizedUsername = AuthUtils.sanitizeUsername(credentials.username);
    if (sanitizedUsername !== credentials.username || sanitizedUsername.length < 3) {
      setError('Uživatelské jméno musí mít alespoň 3 znaky a obsahovat pouze písmena, číslice, tečky, podtržítka a pomlčky');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Client-side password processing
      const processedPassword = await AuthUtils.processPassword(credentials.password);

      // Send to backend
      const response = await fetch('/api/v1/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: AuthUtils.sanitizeUsername(credentials.username),
          email: credentials.email.toLowerCase().trim(),
          password_hash: processedPassword
        })
      });

      const result: AuthResponse = await response.json();

      if (result.success) {
        // Registration successful, auto-login
        const loginResponse = await fetch('/api/v1/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            username: AuthUtils.sanitizeUsername(credentials.username),
            password_hash: processedPassword
          })
        });

        const loginResult: AuthResponse = await loginResponse.json();

        if (loginResult.success && loginResult.user) {
          onSuccess?.(loginResult.user);
        } else {
          setError('Registrace úspěšná, ale automatické přihlášení se nezdařilo. Zkuste se přihlásit.');
        }
      } else {
        setError(result.message || 'Registrace se nezdařila');
      }
    } catch (err) {
      setError('Chyba připojení k serveru');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof RegisterCredentials) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setCredentials(prev => ({ ...prev, [field]: value }));
    setTouched(prev => ({ ...prev, [field]: true }));

    if (field === 'password') {
      const v = AuthUtils.validatePassword(value);
      setPasswordErrors(v.valid ? [] : v.errors);
    }

    if (error) setError('');
  };

  const emailInvalid = touched.email && !AuthUtils.validateEmail(credentials.email);
  const usernameInvalid = touched.username && (AuthUtils.sanitizeUsername(credentials.username) !== credentials.username || credentials.username.length < 3);
  const confirmInvalid = touched.confirmPassword && credentials.confirmPassword !== credentials.password;

  const passwordChecks = [
    { ok: /[A-Z]/.test(credentials.password), label: 'Velké písmeno' },
    { ok: /[a-z]/.test(credentials.password), label: 'Malé písmeno' },
    { ok: /\d/.test(credentials.password), label: 'Číslice' },
    { ok: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(credentials.password), label: 'Speciální znak' },
    { ok: credentials.password.length >= 8, label: 'Min. 8 znaků' }
  ];

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Registrace
      </Typography>

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          margin="normal"
          label="Uživatelské jméno"
          type="text"
          value={credentials.username}
          onChange={handleChange('username')}
          disabled={loading}
          required
          autoComplete="username"
          error={!!usernameInvalid}
          helperText={usernameInvalid ? 'Min. 3 znaky, pouze písmena/číslice . _ -' : 'Pouze písmena, číslice, tečky, podtržítka a pomlčky'}
          sx={{
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#1F1F1F',
            },
          }}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Email"
          type="email"
          value={credentials.email}
          onChange={handleChange('email')}
          disabled={loading}
          required
          autoComplete="email"
          error={!!emailInvalid}
          helperText={emailInvalid ? 'Zadejte platný email' : ''}
          sx={{
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#1F1F1F',
            },
          }}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Heslo"
          type={showPassword ? 'text' : 'password'}
          value={credentials.password}
          onChange={handleChange('password')}
          disabled={loading}
          required
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton aria-label="Zobrazit heslo" onClick={() => setShowPassword(s => !s)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#1F1F1F',
            },
          }}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Potvrzení hesla"
          type={showConfirm ? 'text' : 'password'}
          value={credentials.confirmPassword}
          onChange={handleChange('confirmPassword')}
          disabled={loading}
          required
          error={!!confirmInvalid}
          helperText={confirmInvalid ? 'Hesla se neshodují' : ''}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton aria-label="Zobrazit potvrzení hesla" onClick={() => setShowConfirm(s => !s)} edge="end">
                  {showConfirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#1F1F1F',
            },
          }}
        />

        {/* Požadavky na heslo – živá kontrola */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, auto)' }, gap: 1, mt: 1 }}>
          {passwordChecks.map((c) => (
            <Box key={c.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {c.ok ? <CheckCircle sx={{ fontSize: 16, color: '#2E7D32' }} /> : <CancelOutlined sx={{ fontSize: 16, color: '#BDBDBD' }} />}
              <Typography variant="caption" sx={{ color: c.ok ? '#2E7D32' : '#777' }}>{c.label}</Typography>
            </Box>
          ))}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{ mt: 3, mb: 2, py: 1.4, fontWeight: 700, textTransform: 'none', borderRadius: 2 }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Registruji...
            </>
          ) : (
            'Zaregistrovat se'
          )}
        </Button>

        {onSwitchToLogin && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2">
              Už máte účet?{' '}
              <MuiLink
                component="button"
                type="button"
                onClick={onSwitchToLogin}
                sx={{ cursor: 'pointer', color: '#1F1F1F', fontWeight: 500 }}
              >
                Přihlaste se
              </MuiLink>
            </Typography>
          </Box>
        )}
      </form>
    </Box>
  );
};

export default RegisterForm;
